import type { RealtimeEventKey, RealtimeEventType } from '@/lib/realtime/types';
import type { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import { REALTIME_SUBSCRIBE_STATES } from '@supabase/supabase-js';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { PRIVATE_REALTIME_CHANNEL_CONFIG } from '@/lib/realtime/private-channel';
import { REALTIME } from '@/lib/realtime/types';
import { useAuth } from '@/providers/auth-provider';
import { createClient, syncRealtimeAuth } from '@/utils/supabase/client';
type UseRealtimeOptions<T extends RealtimeEventKey> = {
  /** Callback function when an event is received */
  onEvent: (event: (typeof REALTIME)[T]['payloadType']) => void;
  /** Optional error handler */
  onError?: (error: unknown) => void;
  /** Called after every successful subscription, including silent reconnects. */
  onSubscribed?: () => void;
  /** A channel stopped and a rejoin is scheduled, including the silent closes `onError` skips. */
  onDisconnected?: () => void;
  /** The event key from REALTIME object */
  eventKey: T;
  /** The dynamic identifier(s) - required for all events */
  id: string | number | (string | number)[];
  /** Whether the subscription should be enabled */
  enabled?: boolean;
  /**
   * Subscribe over a dedicated realtime websocket instead of the shared one.
   * Sibling channels on the shared socket push fresh access tokens on every
   * retry, which re-authorizes all channels and stalls their broadcasts.
   */
  dedicatedSocket?: boolean;
};

// Start reconnect attempts quickly so transient auth/channel hiccups heal without noticeable UI lag.
const REALTIME_RETRY_BASE_DELAY_MS = 1_000;
// Cap exponential backoff so a broken subscription keeps retrying, but does not grow indefinitely.
const REALTIME_RETRY_MAX_DELAY_MS = 10_000;
type RealtimeSubscribeCallback = NonNullable<Parameters<RealtimeChannel['subscribe']>[0]>;
type RealtimeSubscribeStatus = Parameters<RealtimeSubscribeCallback>[0];
type RealtimeSubscribeError = Parameters<RealtimeSubscribeCallback>[1];
type ChannelSubscriber = {
  onBroadcast: (payload: unknown) => void;
  onStatus: (status: RealtimeSubscribeStatus, err?: RealtimeSubscribeError) => void;
};
type SharedChannel = {
  channel: RealtimeChannel;
  subscribers: Set<ChannelSubscriber>;
  isSubscribed: boolean;
};

/**
 * Releases a subscription's dedicated client, which nothing else uses. Its auth would otherwise keep
 * refreshing, and recover the session on every tab return, announcing SIGNED_IN to the app's client.
 */
function releaseDedicatedClient(client: SupabaseClient) {
  client.realtime.disconnect();
  void client.auth.stopAutoRefresh();
}
function createReconnectTimer() {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return {
    isScheduled: () => timer !== null,
    schedule(callback: () => void, delay: number) {
      timer = setTimeout(() => {
        timer = null;
        callback();
      }, delay);
    },
    cancel() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    }
  };
}

/**
 * Hook to subscribe to Supabase Realtime events.
 * Automatically manages subscription lifecycle and reconnection.
 *
 * @example
 * // Subscribe to PR updates for a single installation
 * useRealtime({
 *   eventKey: REALTIME_KEYS.PR_V2_UPDATE,
 *   id: installationId,
 *   onEvent: (payload) => {
 *     // payload is typed as PR_V2_UPDATE payload
 *     console.log(payload.pr.number);
 *   }
 * });
 *
 * // Subscribe to PR updates for multiple installations
 * useRealtime({
 *   eventKey: REALTIME_KEYS.PR_V2_UPDATE,
 *   id: [installation1, installation2, installation3],
 *   onEvent: (payload) => {
 *     // Handle updates from any of the installations
 *   }
 * });
 */
export function useRealtime<T extends RealtimeEventKey>({
  onEvent,
  onError = console.error,
  onSubscribed,
  onDisconnected,
  eventKey,
  id,
  // If an array, subscribe to each ID individually
  enabled = true,
  dedicatedSocket = false
}: UseRealtimeOptions<T>) {
  const {
    isAuthenticated,
    isLoading
  } = useAuth();
  const onRealtimeEvent = useEffectEvent(onEvent);
  const onRealtimeError = useEffectEvent(onError);
  const onRealtimeSubscribed = useEffectEvent(() => onSubscribed?.());
  const onRealtimeDisconnected = useEffectEvent(() => onDisconnected?.());
  const serializedIds = JSON.stringify(Array.isArray(id) ? id : [id]);
  const retryAttemptRef = useRef(0);
  const [retryNonce, setRetryNonce] = useState(0);
  const shouldSubscribe = enabled && isAuthenticated && !isLoading;
  useEffect(() => {
    if (!shouldSubscribe) {
      return;
    }
    const eventConfig = REALTIME[eventKey];
    const channelNames = (JSON.parse(serializedIds) as Array<string | number>).map(singleId => `${eventConfig.channelPrefix}:${singleId}`);
    // The channels not yet subscribed in this run. The backoff resets only once none is left, so a
    // healthy channel cannot keep a denied sibling retrying at the base delay.
    const pendingChannels = new Set(channelNames);
    let leaveChannels: Array<() => void> = [];
    let isCancelled = false;
    const reconnectTimer = createReconnectTimer();
    // Every subscriber to a shared channel hears its statuses and broadcasts from one loop, so a
    // throwing onError must not escape and cut off the subscribers after this one.
    const notifyError = (error: unknown) => {
      try {
        onRealtimeError(error);
      } catch (onErrorFailure) {
        console.error(error, onErrorFailure);
      }
    };
    const scheduleReconnect = (error_0: unknown, options?: {
      notify?: boolean;
    }) => {
      if (isCancelled) {
        return;
      }
      if (options?.notify ?? true) {
        notifyError(error_0);
      }
      // Reported like onSubscribed: a throw would cut off the status loop and this rejoin.
      try {
        onRealtimeDisconnected();
      } catch (onDisconnectedFailure) {
        notifyError(onDisconnectedFailure);
      }
      if (reconnectTimer.isScheduled()) {
        return;
      }
      const retryDelayMs = Math.min(REALTIME_RETRY_MAX_DELAY_MS, REALTIME_RETRY_BASE_DELAY_MS * 2 ** retryAttemptRef.current);
      reconnectTimer.schedule(() => {
        if (!isCancelled) {
          retryAttemptRef.current += 1;
          setRetryNonce(current => current + 1);
        }
      }, retryDelayMs);
    };
    const createSubscriber = (channelName: string): ChannelSubscriber => ({
      onBroadcast: payload => {
        try {
          onRealtimeEvent(payload as (typeof REALTIME)[T]['payloadType']);
        } catch (err) {
          notifyError(err);
        }
      },
      onStatus: (status, err_0) => {
        if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
          pendingChannels.delete(channelName);
          if (pendingChannels.size === 0) {
            retryAttemptRef.current = 0;
          }
          // A throw would stop the channel's status loop before the other subscribers, or abort a
          // join that replays SUBSCRIBED before returning its leave function, so report it instead.
          try {
            onRealtimeSubscribed();
          } catch (err_1) {
            notifyError(err_1);
          }
          return;
        }

        // Supabase can emit CLOSED without an error payload during normal reconnect churn or auth
        // transitions. Reconnect silently instead of surfacing a noisy "Unknown error" message.
        if (status === REALTIME_SUBSCRIBE_STATES.CLOSED && !err_0) {
          scheduleReconnect(new Error(`Realtime channel ${channelName} closed`), {
            notify: false
          });
          return;
        }
        if (status === REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR || status === REALTIME_SUBSCRIBE_STATES.TIMED_OUT || status === REALTIME_SUBSCRIBE_STATES.CLOSED) {
          scheduleReconnect(new Error(`Failed to connect to Realtime channel ${channelName}. Status: ${status}. Error: ${err_0?.message || 'Unknown error'}`));
        }
      }
    });
    let supabaseClient: SupabaseClient | null = null;
    let stopWaitingForSession: (() => void) | null = null;
    void (async () => {
      try {
        const supabase = await syncRealtimeAuth(null, {
          dedicated: dedicatedSocket
        });
        supabaseClient = supabase;
        if (isCancelled) {
          if (dedicatedSocket && supabase) {
            releaseDedicatedClient(supabase);
          }
          return;
        }
        if (!supabase) {
          // Private channels deny a join without a user token, so wait for a session instead of
          // retrying on a timer. INITIAL_SESSION reports a session that arrived in the meantime.
          const {
            data: {
              subscription
            }
          } = createClient().auth.onAuthStateChange((_event, session) => {
            if (session) {
              setRetryNonce(current_0 => current_0 + 1);
            }
          });
          stopWaitingForSession = () => subscription.unsubscribe();
          return;
        }
        leaveChannels = channelNames.map(channelName_0 => joinSharedChannel(supabase, channelName_0, eventConfig.eventType, createSubscriber(channelName_0)));
      } catch (error_1) {
        scheduleReconnect(error_1);
      }
    })();
    return () => {
      isCancelled = true;
      reconnectTimer.cancel();
      stopWaitingForSession?.();
      leaveChannels.forEach(leave => leave());
      if (dedicatedSocket && supabaseClient) {
        releaseDedicatedClient(supabaseClient);
      }
    };
  }, [eventKey, retryNonce, serializedIds, shouldSubscribe, dedicatedSocket]);
  return {
    /** Rejoin at once, skipping the backoff. */
    reconnect: () => {
      retryAttemptRef.current = 0;
      setRetryNonce(current_1 => current_1 + 1);
    }
  };
}

/**
 * Each client's open channels by name. realtime-js hands every caller the listed channel for a
 * topic, registers a subscribe callback only while that channel is closed, and closes it for
 * everyone on unsubscribe. So the hooks on one topic share a channel, and only the last one to
 * leave closes it. A dedicated-socket hook brings its own client, so it gets its own channels.
 */
const sharedChannels = new WeakMap<SupabaseClient, Map<string, SharedChannel>>();

/**
 * Adds `subscriber` to the client's channel for `channelName`, opening the channel when there is
 * none, and returns the subscriber's leave function. A failed channel is dropped at once, so each
 * subscriber's retry opens a fresh one.
 */
function joinSharedChannel(client: SupabaseClient, channelName: string, eventType: RealtimeEventType, subscriber: ChannelSubscriber): () => void {
  const channels = sharedChannels.get(client) ?? new Map<string, SharedChannel>();
  sharedChannels.set(client, channels);
  const shared = channels.get(channelName) ?? openSharedChannel(client, channels, channelName, eventType);
  shared.subscribers.add(subscriber);
  if (shared.isSubscribed) {
    subscriber.onStatus(REALTIME_SUBSCRIBE_STATES.SUBSCRIBED);
  }
  return () => {
    shared.subscribers.delete(subscriber);
    // A failed channel is already closed. Leaving it again would drop a fresh channel for the
    // topic as well, because realtime-js removes closed channels by topic.
    if (shared.subscribers.size === 0 && channels.get(channelName) === shared) {
      channels.delete(channelName);
      shared.channel.unsubscribe();
    }
  };
}

/**
 * Opens the client's channel for `channelName` and passes its broadcasts and statuses to every
 * subscriber that joins it. A failure drops the channel from `channels`.
 */
function openSharedChannel(client: SupabaseClient, channels: Map<string, SharedChannel>, channelName: string, eventType: RealtimeEventType): SharedChannel {
  const shared: SharedChannel = {
    channel: client.channel(channelName, {
      config: PRIVATE_REALTIME_CHANNEL_CONFIG
    }),
    subscribers: new Set<ChannelSubscriber>(),
    isSubscribed: false
  };
  channels.set(channelName, shared);
  // Bound once for every subscriber, since each REALTIME channel prefix carries one event type.
  shared.channel.on('broadcast', {
    event: eventType
  }, event => {
    for (const current of shared.subscribers) {
      current.onBroadcast(event.payload);
    }
  }).subscribe((status, err) => {
    // React Strict Mode intentionally mounts and tears down effects twice in dev.
    // Suppress the synthetic CLOSED callback from our own unsubscribe so logs only
    // reflect genuine channel failures. Every unsubscribe, including the one below that leaves
    // a failed channel, drops the channel from the map first.
    if (channels.get(channelName) !== shared) {
      return;
    }
    if (status === REALTIME_SUBSCRIBE_STATES.SUBSCRIBED) {
      shared.isSubscribed = true;
    } else {
      channels.delete(channelName);
      // realtime-js keeps handing out an errored or timed-out channel for its topic until it is
      // left. A CLOSED channel is already gone.
      if (status !== REALTIME_SUBSCRIBE_STATES.CLOSED) {
        shared.channel.unsubscribe();
      }
    }
    for (const current of shared.subscribers) {
      current.onStatus(status, err);
    }
  });
  return shared;
}